import axios from 'axios';
import { userToken } from './features/user';
import { cancelPasskeyCeremony, signStepUpOptions, passkeyErrorMessage } from './Passkey';

const empty = {view: null, busy: false, error: '', selected: null, challenge: null, code: '', enrollment: false, pending: false, remaining: 0};
const failure = (code) => Object.assign(new Error(code), {response: {status: 403, data: {responseStatus: 'Exception', message: code}}});
const errorKey = (error) => {
  if (error?.passkeyMessage) return error.passkeyMessage;
  const key = error?.response?.data?.message || error?.message;
  return typeof key === 'string' && /^[A-Za-z_]+$/.test(key) ? key : 'SERVER_ERROR';
};

/** Queue is tab-local; authorization and usage counts are exclusively server-side. */
/**
 * ⚠️ **השיטות שפתיחת האתגר שלהן אינה מוציאה דבר.**
 *
 * מראה של `StepUpFactors.start` ב-SSO: מפתח גישה פותח טקס בדפדפן, יישומון וקוד שחזור
 * מייצרים אתגר ריק, והגורם המחושב פותח טופס. כל השאר הוא שורת שליחה שגובה מהתקציב
 * ומוציאה קוד בפועל.
 *
 * **הרשימה סגורה, והברירה היא "שולח"**: שיטה שתתווסף בשרת ולא כאן תדרוש לחיצה, וזו
 * הטעות הזולה מבין השתיים.
 */
const SILENT_START = ['PASSKEY', 'APP', 'RecoveryCodes', 'BILLING_IDENTITY'];

export function dispatchesOnStart(method) {
  return !SILENT_START.includes(method?.type);
}

export function createStepUpController({send, getToken, sign, abortCeremony}) {
  let state = empty;
  let active = null;
  const queue = [];
  const listeners = new Set();
  const update = (patch) => {state = {...state, ...patch}; listeners.forEach(listener => listener());};
  /**
   * הזמן שנותר לאתגר הפתוח, שנייה אחר שנייה.
   *
   * ⚠️ **אתגר חי שתי דקות לכל היותר** (`StepUpStore.start`). בלי מספר שרץ הלקוח מגלה
   * את התפוגה רק בלחיצה על אישור, ומקבל תשובה שנראית ככישלון אימות. הדד-ליין מעוגן
   * כאן מהזמן שהשרת מסר, ונקרא מחדש מהשעון בכל פעימה - כך לשונית שישנה אינה מציגה
   * מספר שקפא.
   */
  let ticker = null;
  const stopExpiry = () => {if (ticker) {clearInterval(ticker); ticker = null;}};
  const startExpiry = (expiresAt) => {
    stopExpiry();
    const deadline = Date.parse(expiresAt);
    if (!Number.isFinite(deadline)) {update({remaining: 0}); return;}
    const tick = () => {
      const left = Math.max(0, Math.ceil((deadline - Date.now()) / 1000));
      update({remaining: left});
      if (!left) stopExpiry();
    };
    tick();
    if (deadline > Date.now()) ticker = setInterval(tick, 1000);
  };
  const api = async (endpoint, body, task) => {
    if (getToken() !== task.token) throw failure('STEP_UP_STATE_CHANGED');
    const reply = await send(endpoint, body, task.token);
    if (getToken() !== task.token) throw failure('STEP_UP_STATE_CHANGED');
    if (reply?.responseStatus !== 'OK') throw failure(reply?.message || 'SERVER_ERROR');
    return reply;
  };
  const finish = (task, allowed, result, error) => {
    if (task.done) return;
    task.done = true;
    clearTimeout(task.timer);
    task.signal?.removeEventListener('abort', task.abort);
    const index = queue.indexOf(task); if (index >= 0) queue.splice(index, 1);
    if (active === task) {abortCeremony(); stopExpiry(); active = null; update(empty);}
    if (!allowed && !task.enrollment && !task.pending && getToken() === task.token) {
      void api('cancelStepUp', {intentId: task.view.intentId}, task).catch(() => {});
    }
    if (task.pending) {
      if (allowed && getToken() === task.token) task.resolve({responseStatus: 'OK', ...result});
      else {
        const pending = failure('STEP_UP_PENDING');
        pending.response.status = 202; pending.response.data.operationId = task.view.intentId;
        task.reject(error || pending);
      }
    } else task.resolve(allowed && getToken() === task.token);
    advance();
  };
  const advance = () => {
    if (active || !queue.length) return;
    const task = active = queue[0];
    update({...empty, view: task.view, busy: !task.enrollment, enrollment: task.enrollment,
      error: task.enrollment ? 'STEP_UP_ENROLLMENT_REQUIRED' : ''});
    if (task.pending) {
      update({pending: true});
      const poll = async () => {
        try {
          const reply = await api('getStepUpResult', {intentId: task.view.intentId}, task);
          if (task.done || active !== task) return;
          if (reply.intentId !== task.view.intentId) throw failure('STEP_UP_STATE_CHANGED');
          if (reply.status === 'committed') {finish(task, true, reply.result); return;}
          if (reply.status === 'failed') {finish(task, false, null, failure(reply.result?.reason || 'CONTACT_STATE_CHANGED')); return;}
          if (reply.status !== 'pendingCompletion') throw failure('STEP_UP_STATE_CHANGED');
          task.delay = Math.min(5000, task.delay + 500);
        } catch (error) {
          if (task.done || active !== task) return;
          if (!error.response?.status || error.response.status >= 500 || error.response.status === 429) task.delay = 5000;
          else {finish(task, false, null, error); return;}
        }
        if (!task.done) task.timer = setTimeout(poll, task.delay);
      };
      void poll(); return;
    }
    if (task.enrollment) return;
    let only = null;
    void api('getStepUpState', {intentId: task.view.intentId}, task).then(view => {
      if (active !== task) return;
      if (['ready', 'committed', 'pendingCompletion', 'failed'].includes(view.status)) {finish(task, true); return;}
      if (view.status !== 'verificationRequired' || !view.methods?.length) throw failure('STEP_UP_STATE_CHANGED');
      update({view});
      /*
       * שיטה יחידה אינה בחירה - היא נבחרת מאליה, והרשימה אינה מצוירת. ראה `StepUpDialog`.
       *
       * ⚠️ **ורק כשפתיחת האתגר אינה מוציאה דבר.** שיטה ששולחת קוד הופכת את עצם
       * פתיחת החלון לשליחה: היא גובה מתקציב השליחות, ולקוח שפתח את המסך כמה פעמים
       * נחסם בלי שעשה דבר. אצלה מצויר כפתור שליחה אחד. ראה `dispatchesOnStart`.
       */
      if (view.methods.length === 1 && !dispatchesOnStart(view.methods[0])) only = view.methods[0];
    }).catch(error => {if (active === task) update({error: errorKey(error)});})
      .finally(() => {
        if (active !== task) return;
        update({busy: false});
        // אחרי `busy`, ולא לפני: `choose` יוצא בשקט כשהמסך עסוק.
        if (only) void choose(only);
      });
  };
  const choose = async (method) => {
    const task = active;
    if (!task || state.busy) return;
    stopExpiry();
    update({busy: true, error: '', selected: method, challenge: null, code: '', remaining: 0});
    try {
      const challenge = await api('startStepUpVerification', {intentId: task.view.intentId, methodId: method.id}, task);
      // לפני ההשמה, כדי שלא ייפתח רגע שבו יש אתגר והספירה עדיין על אפס - כלומר "פג".
      if (active === task) {startExpiry(challenge.expiresAt); update({challenge});}
    } catch (error) {if (active === task) update({error: errorKey(error)});}
    finally {if (active === task) update({busy: false});}
  };
  return {
    subscribe: listener => {listeners.add(listener); return () => listeners.delete(listener);},
    getSnapshot: () => state,
    authorize(view, token, enrollment = false, signal) {
      if (signal?.aborted) return Promise.resolve(false);
      return new Promise(resolve => {
        const task = {view, token, enrollment, signal, resolve, done: false};
        task.abort = () => finish(task, false);
        signal?.addEventListener('abort', task.abort, {once: true});
        queue.push(task); advance();
      });
    },
    waitForCompletion(intentId, token, signal) {
      if (signal?.aborted) return Promise.reject(failure('STEP_UP_PENDING'));
      return new Promise((resolve, reject) => {
        const task = {view: {intentId}, token, signal, resolve, reject, done: false, pending: true, delay: 1000};
        task.abort = () => finish(task, false);
        signal?.addEventListener('abort', task.abort, {once: true});
        queue.push(task); advance();
      });
    },
    setCode: code => update({code}),
    cancel: () => {if (active) finish(active, false);},
    choose,
    /**
     * אותו טקס, מהתחלה - מה שלחיצה חוזרת על השיטה עושה ברשימה שאינה מצוירת.
     *
     * הכוונה נשארת אותה כוונה, ומה שנפתח מחדש הוא האתגר בלבד: `StepUpStore.start`
     * מבטל את הקודם ומוציא קוד חדש אם יש מה לשלוח.
     */
    restart() {
      const method = state.selected || (state.view?.methods?.length === 1 ? state.view.methods[0] : null);
      return method ? choose(method) : Promise.resolve();
    },
    async submit() {
      const task = active, challenge = state.challenge;
      if (!task || !challenge || state.busy) return;
      update({busy: true, error: ''});
      try {
        /**
         * ⚠️ **תפוגת אתגר אינה תפוגת כוונה.** `STEP_UP_EXPIRED` הוא מצב סופי שסוגר
         * את החלון, וכאן הכוונה עדיין חיה ומקבלת אתגר חדש - "התחל מחדש".
         */
        if (Date.parse(challenge.expiresAt) <= Date.now()) throw failure('STEP_UP_CHALLENGE_EXPIRED');
        let proof;
        if (challenge.data.type === 'PASSKEY') {
          try {proof = {passkeyResponse: await sign(challenge.data.options)};}
          catch (error) {throw Object.assign(failure('PASSKEY_FAILED'), {passkeyMessage: passkeyErrorMessage(error)});}
        } else proof = {code: state.code.trim()};
        if (active !== task) return;
        await api('verifyStepUpVerification', {intentId: task.view.intentId, challengeId: challenge.challengeId, ...proof}, task);
        if (active === task) finish(task, true);
      } catch (error) {
        if (active !== task) return;
        update({error: errorKey(error), code: ''});
        if (state.error === 'SERVER_ERROR') {
          try {
            const result = await api('getStepUpState', {intentId: task.view.intentId}, task);
            if (active === task && ['ready', 'committed'].includes(result.status)) finish(task, true);
          } catch { /* A timeout is not proof. */ }
        }
      } finally {if (active === task) update({busy: false});}
    },
  };
}

export const stepUp = createStepUpController({
  getToken: userToken, sign: signStepUpOptions, abortCeremony: cancelPasskeyCeremony,
  send: async (endpoint, body, token) => (await axios.post(`/usersApi/${endpoint}`, body, {headers: {token}, timeout: 15000})).data,
});

export function installStepUpRecovery(client = axios, controller = stepUp, getToken = userToken) {
  const recover = async (response, error) => {
    const config = response?.config || error?.config;
    const data = response?.data || error?.response?.data;
    const code = data?.exception || data?.message;
    const passThrough = () => {if (error) throw error; return response;};
    if (!config || config.method?.toLowerCase() !== 'post' ||
      !['STEP_UP_REQUIRED', 'STEP_UP_ENROLLMENT_REQUIRED', 'STEP_UP_PENDING'].includes(code) ||
      (config._stepUpRetried && code !== 'STEP_UP_PENDING')) return passThrough();
    let url;
    try {url = new URL(config.url, config.baseURL || window.location.origin);} catch {return passThrough();}
    if (url.origin !== window.location.origin || url.search || (config.params && Object.keys(config.params).length) ||
      !/^\/(?:api\/sso\/)?usersApi\/[^/]+$/.test(url.pathname) ||
      /\/(getStepUpState|getStepUpResult|startStepUpVerification|verifyStepUpVerification|cancelStepUp)$/.test(url.pathname)) return passThrough();
    const enrollment = code === 'STEP_UP_ENROLLMENT_REQUIRED';
    if (!enrollment && !/^[a-f0-9-]{36}$/.test(data.stepUp?.intentId || '')) return passThrough();
    const token = config.headers?.get?.('token') || config.headers?.token;
    if (!token || token !== getToken()) throw failure('STEP_UP_STATE_CHANGED');
    if (code === 'STEP_UP_PENDING') {
      if (data.responseStatus !== 'Pending') return passThrough();
      const result = await controller.waitForCompletion(data.stepUp.intentId, token, config.signal);
      if (token !== getToken()) throw failure('STEP_UP_STATE_CHANGED');
      return {...response, status: 200, data: result};
    }
    const allowed = await controller.authorize(data.stepUp || {}, token, enrollment, config.signal);
    if (!allowed || enrollment) throw failure(enrollment ? code : 'STEP_UP_CANCELLED');
    if (token !== getToken()) throw failure('STEP_UP_STATE_CHANGED');
    // Axios already serialized config.data before the first send; preserve those exact bytes.
    return client.request({...config, _stepUpRetried: true,
      headers: {...config.headers, 'X-SSO-Operation-Id': data.stepUp.intentId}});
  };
  const id = client.interceptors.response.use(response => recover(response), error => recover(error.response, error));
  return () => client.interceptors.response.eject(id);
}
